import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Bell, Check, Trash2 } from 'lucide-react';

const typeIcons = { release_approved: '✅', release_rejected: '❌', release_submitted: '📦', payment_paid: '💰', payment_requested: '💳', ticket_reply: '💬', oac_updated: '📺', takedown: '🔴', statement: '📊', general: '🔔' };

export default function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0); // forces time re-render
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => { try { const { data } = await api.get('/notifications'); setNotifs(data.notifications || []); setUnread(data.unread_count || 0); } catch (e) {} };

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  // Re-render every 60s to update times
  useEffect(() => { const iv = setInterval(() => setTick(t => t + 1), 60000); return () => clearInterval(iv); }, []);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);

  const readAll = async () => { try { await api.post('/notifications/read-all'); setUnread(0); setNotifs(p => p.map(n => ({ ...n, is_read: 1 }))); } catch (e) {} };
  const deleteAll = async () => { if (!window.confirm('Eliminare tutte le notifiche?')) return; try { await api.delete('/notifications/all'); setNotifs([]); setUnread(0); } catch (e) {} };

  const clickNotif = async (n) => {
    if (!n.is_read) { try { await api.post(`/notifications/${n.id}/read`); setUnread(p => Math.max(0, p - 1)); setNotifs(p => p.map(x => x.id === n.id ? { ...x, is_read: 1 } : x)); } catch (e) {} }
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  const ago = (d) => {
    const now = Date.now();
    const then = new Date(d).getTime();
    const m = Math.floor((now - then) / 60000);
    if (m < 1) return 'adesso';
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} ${h === 1 ? 'ora' : 'ore'} fa`;
    const g = Math.floor(h / 24);
    return `${g} ${g === 1 ? 'giorno' : 'giorni'} fa`;
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
        <Bell size={20} />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border z-50 max-h-[70vh] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-bold text-sm">Notifiche</h3>
            <div className="flex items-center gap-3">
              {unread > 0 && <button onClick={readAll} className="text-[10px] text-blue-500 hover:underline flex items-center gap-1"><Check size={10} />Lette</button>}
              {notifs.length > 0 && <button onClick={deleteAll} className="text-[10px] text-red-400 hover:underline flex items-center gap-1"><Trash2 size={10} />Elimina tutte</button>}
            </div>
          </div>
          <div className="overflow-y-auto max-h-[55vh]">
            {notifs.length === 0 ? <p className="text-center text-gray-400 text-sm py-8">Nessuna notifica</p> : notifs.map(n => (
              <div key={n.id} onClick={() => clickNotif(n)} className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition ${!n.is_read ? 'bg-blue-50/50' : ''}`}>
                <div className="flex items-start gap-2">
                  <span className="text-lg flex-shrink-0">{typeIcons[n.type] || '🔔'}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.is_read ? 'font-medium' : 'text-gray-600'}`}>{n.title}</p>
                    {n.message && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>}
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{ago(n.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
